<template>
  <div
    ref="reviewsBlockObserverTarget"
    class="mt-32 scroll-mt-[51px] focus:outline-none md:scroll-mt-[121px] lg:scroll-mt-[81px]"
    tabindex="-1"
  >
    <h2 v-if="title && !hideMainTitle" class="heading-2 mb-16 md:mb-32">
      {{ title }}
    </h2>

    <ReviewsDetailsSummary
      class="mb-32 md:hidden"
      :pending="pendingSummary"
      :rating
      :summary="ratingSummary"
    />

    <!-- Mobile Highlighted AI Tags -->
    <div v-if="hasHighlightedAItagsVariation" class="md:hidden">
      <RevSkeleton
        v-if="aiSummaryPending"
        class="mb-32"
        height="124px"
        shape="rectangle"
        width="100%"
      />

      <HighlightedAITags
        v-if="!aiSummaryPending && aiSummary?.tags"
        class="mb-32"
        :reviews-count="aiSummary.reviewsCount"
        :tags="aiSummary.tags"
        :updated-at="aiSummary.updatedAt"
      />
    </div>

    <RevSkeleton
      v-if="isPendingReviewsWithAttachments"
      class="mb-32 md:hidden"
      height="236px"
      shape="rectangle"
      width="100%"
    />

    <div v-if="hasReviewsWithAttachments" class="mb-32 md:hidden">
      <ReviewsPhotosCarousel :reviews="reviewsWithAttachments" />
    </div>

    <div
      v-if="hasAISummaryText"
      class="bg-surface-default-low -mx-24 mb-32 px-24 pb-20 pt-32 md:hidden"
    >
      <AISummary :ai-summary :pending="aiSummaryPending" />
    </div>

    <div class="grid-standard md:gap-24 lg:gap-56">
      <!-- Desktop side section -->
      <aside class="hidden md:col-span-1 md:block lg:col-span-3">
        <ReviewsDetailsSummary
          class="mb-56"
          :pending="pendingSummary"
          :rating
          :summary="ratingSummary"
        />

        <div
          class="max-h-[calc(100vh-150px)] self-start overflow-y-auto md:sticky md:top-[106px]"
          data-scrollable-fade
        >
          <Filters class="pb-32" :merchant-id :reset-filters />
        </div>
      </aside>

      <div
        class="flex flex-col md:col-span-3 lg:col-span-9"
        data-test="reviews-list"
      >
        <!-- Mobile Filters -->
        <div
          ref="mobileFiltersRef"
          class="mb-16 w-full max-w-[400px] scroll-mt-[120px] items-center gap-8 md:hidden"
        >
          <div class="mb-8 flex items-start justify-between">
            <h2 class="heading-2">
              {{ i18n(translations.reviewsSectionTitle) }}
            </h2>
            <MobileFilters :merchant-id :reset-filters />
          </div>

          <!-- Mobile AI Tags -->
          <div v-if="hasAITagsVariation">
            <RevSkeleton
              v-if="aiSummaryPending"
              class="mb-20 mt-16"
              height="124px"
              shape="rectangle"
              width="100%"
            />
            <AITags
              v-if="!aiSummaryPending && aiSummary?.tags"
              class="mb-20 mt-16"
              is-mobile
              :reviews-count="aiSummary.reviewsCount"
              :tags="aiSummary.tags"
              :updated-at="aiSummary.updatedAt"
            />
          </div>

          <Sort id="product-page-sort-select" />
        </div>

        <RevSkeleton
          v-if="isPendingReviewsWithAttachments"
          class="mb-32 hidden md:block"
          height="236px"
          shape="rectangle"
          width="100%"
        />

        <div v-if="hasReviewsWithAttachments" class="mb-32 hidden md:block">
          <ReviewsPhotosCarousel :reviews="reviewsWithAttachments" />
        </div>

        <AISummary
          v-if="hasAISummaryText"
          :ai-summary
          class="mb-32 max-md:hidden"
          :pending="aiSummaryPending"
        />

        <!-- Desktop Highlighted AI Tags -->
        <div v-if="hasHighlightedAItagsVariation" class="hidden md:block">
          <RevSkeleton
            v-if="aiSummaryPending"
            class="mb-32"
            height="124px"
            shape="rectangle"
            width="100%"
          />

          <HighlightedAITags
            v-if="!aiSummaryPending && aiSummary?.tags"
            class="mb-32"
            :reviews-count="aiSummary.reviewsCount"
            :tags="aiSummary.tags"
            :updated-at="aiSummary.updatedAt"
          />
        </div>

        <!-- Desktop Sort -->
        <div
          ref="reviewsTitleRef"
          class="mb-12 hidden scroll-mt-[145px] items-start justify-between md:flex"
        >
          <h2 class="heading-2">
            {{ i18n(translations.reviewsSectionTitle) }}
          </h2>
          <Sort v-if="reviews.length > 0" id="product-page-sort-select" />
        </div>

        <!-- Desktop AI Tags -->
        <div v-if="hasAITagsVariation">
          <RevSkeleton
            v-if="aiSummaryPending"
            class="mb-20 hidden md:block"
            height="124px"
            shape="rectangle"
            width="100%"
          />
          <AITags
            v-if="!aiSummaryPending && aiSummary?.tags"
            class="mb-20 hidden md:block"
            :reviews-count="aiSummary.reviewsCount"
            :tags="aiSummary.tags"
            :updated-at="aiSummary.updatedAt"
          />
        </div>

        <div v-if="reviews.length > 0 || reviewsPending">
          <ReviewsList :is-pending="reviewsPending" :reviews />

          <div
            v-if="previousReviewsCursor || nextReviewsCursor"
            class="mt-20 flex w-full items-center justify-end gap-12"
          >
            <RevButtonIcon
              :aria-label="i18n(translations.previousReviews)"
              :disabled="!previousReviewsCursor"
              :icon="IconChevronLeft"
              size="medium"
              variant="primary"
              @click="handlePaginationClick(previousReviewsCursor, 'previous')"
            />
            <RevButtonIcon
              :aria-label="i18n(translations.nextReviews)"
              :disabled="!nextReviewsCursor"
              :icon="IconChevronRight"
              size="medium"
              variant="primary"
              @click="handlePaginationClick(nextReviewsCursor, 'next')"
            />
          </div>
        </div>

        <div
          v-else
          class="flex h-full flex-col items-center justify-center gap-4"
        >
          <p class="text-static-default-mid heading-2">
            {{ i18n(translations.noReviewsForFiltersTitle) }}
          </p>

          <div class="flex flex-col items-center justify-center">
            <p class="text-static-default-low body-1">
              {{ i18n(translations.noReviewsForFiltersDescription) }}
            </p>

            <RevButton
              class="mt-24"
              :icon="IconTrash"
              size="small"
              variant="secondary"
              @click="resetFilters += 1"
            >
              {{ i18n(translations.resetFilters) }}
            </RevButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { navigateTo, useRoute } from '#imports'
import { computed, onBeforeUnmount, provide, ref, useTemplateRef } from 'vue'

import type { ReviewsAISummary } from '@backmarket/http-api/src/api-specs-reviews/types/ai-summary'
import type { Rating } from '@backmarket/http-api/src/api-specs-reviews/types/rating'
import type { ReviewRatingsSummary } from '@backmarket/http-api/src/api-specs-reviews/types/review'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { useClientOnlyComputedBreakpoint } from '@backmarket/utils/composables/useClientOnlyComputedBreakpoint'
import { Breakpoint } from '@backmarket/utils/dom/getCurrentBreakpoint'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { removeEmptyValuesInObject } from '@backmarket/utils/object/removeEmptyValuesInObject'
import type { Maybe } from '@backmarket/utils/types/Maybe'
import { RevButton } from '@ds/components/Button'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevSkeleton } from '@ds/components/Skeleton'
import { IconChevronLeft } from '@ds/icons/IconChevronLeft'
import { IconChevronRight } from '@ds/icons/IconChevronRight'
import { IconTrash } from '@ds/icons/IconTrash'
import { useIntersectionObserver } from '@vueuse/core'

import { TRACKING_PAGE_TYPE_BY_ROUTE_NAME } from '~/constants/trackingPageType'
import { useReviewsWithAttachments } from '~/scopes/reviews/reviews-display/composables/useReviewsWithAttachments'

import AISummary from '../../../ai-summary/components/AISummary/AISummary.vue'
import AITags from '../../../ai-summary/components/AITags/AITags.vue'
import HighlightedAITags from '../../../ai-summary/components/HighlightedAITags/HighlightedAITags.vue'
import { TRACKING_ZONE, scrollToTargetKey } from '../../constants'
import type { DisplayedReview } from '../../models/reviews'
import Filters from '../Filters/Filters.vue'
import MobileFilters from '../Filters/MobileFilters.vue'
import ReviewsDetailsSummary from '../ReviewsDetailsSummary/ReviewsDetailsSummary.vue'
import ReviewsList from '../ReviewsList/ReviewsList.vue'
import ReviewsPhotosCarousel from '../ReviewsPhotosCarousel/ReviewsPhotosCarousel.vue'
import Sort from '../Sort/Sort.vue'

import translations from './ReviewsBlock.translations'

export type ReviewsBlock = {
  productTitle?: string
  mainTitle?: string
  hideMainTitle?: boolean
  reviews: DisplayedReview[]
  reviewsPending?: boolean
  rating: Maybe<Rating>
  trackingInfo?: Record<string, unknown>
  merchantId?: string
  merchantUrl?: string
  nextReviewsCursor?: string
  previousReviewsCursor?: string
  productId: string | undefined
  ratingSummary: Maybe<ReviewRatingsSummary>
  pendingSummary: boolean
  aiSummary: ReviewsAISummary | undefined | null
  aiSummaryPending: boolean
  aiSummaryEnabled?: boolean
}

const props = withDefaults(defineProps<ReviewsBlock>(), {
  trackingInfo: () => ({}),
  mainTitle: undefined,
  hideMainTitle: false,
  productTitle: undefined,
  merchantId: undefined,
  merchantUrl: undefined,
  nextReviewsCursor: undefined,
  previousReviewsCursor: undefined,
  rating: undefined,
})

const route = useRoute()
const i18n = useI18n()
const { trackReviewBlockImpression, trackClick } = useTracking()
const breakpoint = useClientOnlyComputedBreakpoint()
const experiments = useExperiments()

const { reviewsWithAttachments, isPendingReviewsWithAttachments } =
  useReviewsWithAttachments()

const reviewsBlockObserverTarget = useTemplateRef('reviewsBlockObserverTarget')
const mobileFiltersRef = useTemplateRef('mobileFiltersRef')
const reviewsTitleRef = useTemplateRef('reviewsTitleRef')
const resetFilters = ref(0)

const routeName = route.name?.toString() || ''
const pageType = TRACKING_PAGE_TYPE_BY_ROUTE_NAME[routeName]

const hasReviewsWithAttachments = computed(
  () =>
    !!reviewsWithAttachments.value && reviewsWithAttachments.value.length > 0,
)

const hasPhotosCarousel = computed(() =>
  !isPendingReviewsWithAttachments.value
    ? hasReviewsWithAttachments.value
    : 'pending',
)

const hasPhotosInReviewsList = computed(
  () =>
    props.reviews.filter((review) => !isEmpty(review.attachments)).length > 0,
)

const hasAISummaryText = computed(
  () =>
    experiments['experiment.reviewsAiSummary'] === 'withAIReview' &&
    (props.aiSummary?.text || props.aiSummaryPending),
)

const hasAITagsVariation = computed(
  () => experiments['experiment.reviewsNewAITags'] === 'withAITags',
)

const hasHighlightedAItagsVariation = computed(
  () => experiments['experiment.reviewsNewAITags'] === 'withHighlightedAITags',
)

const blockTrackingInfo = computed(() => {
  const trackingData = {
    ...props.trackingInfo,
    pageType,
    averageRate: props.rating?.averageRate,
    count: props.rating?.count,
    hasPhotosCarousel: hasPhotosCarousel.value,
    hasPhotosInReviewsList: hasPhotosInReviewsList.value,
    hasAISummary: !!props.aiSummary,
  }

  return removeEmptyValuesInObject(trackingData)
})

const title = computed(() => {
  if (props.mainTitle) return props.mainTitle
  if (!props.productTitle) return ''

  return i18n(translations.title, { title: props.productTitle })
})

const scrollTarget = computed(() =>
  breakpoint.value < Breakpoint.MD
    ? mobileFiltersRef.value
    : reviewsTitleRef.value,
)

function scrollToTarget() {
  setTimeout(
    () =>
      scrollTarget.value?.scrollIntoView({
        behavior: 'auto',
        block: 'start',
      }),
    0,
  )
}

// Provide the scrollToTarget function to Filters components
provide(scrollToTargetKey, scrollToTarget)

async function handlePaginationClick(
  cursor: string | undefined,
  direction: string,
) {
  if (!cursor) return

  scrollToTarget()

  trackClick({
    pageType: TRACKING_PAGE_TYPE_BY_ROUTE_NAME[routeName],
    zone: TRACKING_ZONE,
    name: `review_pagination_${direction}`,
  })

  await navigateTo({
    ...route,
    hash: route.hash,
    query: {
      ...route.query,
      cursor,
    },
  })
}

const { stop } = useIntersectionObserver(
  reviewsBlockObserverTarget,
  ([{ isIntersecting }]) => {
    if (isIntersecting) {
      trackReviewBlockImpression(blockTrackingInfo.value)
      stop()
    }
  },
  {
    threshold: 0.05,
  },
)

onBeforeUnmount(() => {
  stop()
})
</script>

<style scoped>
[data-scrollable-fade] {
  mask: linear-gradient(
    to bottom,
    black 0%,
    black calc(100% - 2rem),
    transparent 100%
  );
  -webkit-mask: linear-gradient(
    to bottom,
    black 0%,
    black calc(100% - 2rem),
    transparent 100%
  );
}
</style>
